import { assign, fromPromise, setup } from 'xstate';
import { firstValueFrom } from 'rxjs';

import reservationService from '@/services/reservationService';
import type { ReservationValidations } from '@/data/models/reservation.model';

export type ValidateReservationInput = {
  reservation: unknown;
};

export type ValidateReservationContext = {
  reservation: unknown;
  result: ReservationValidations | null;
};

/**
 * Runs POST /validate for a reservation payload. Used while the main form is in `validating`.
 *
 * TODO(dead code): `validateReservationMachine` is never invoked — the main form
 * machine (`reservationFormMain.machine.ts`) calls `runBundledValidation` directly.
 * It is only re-exported from machines/index.ts. Safe to remove the whole file
 * (machine + `ValidateReservationInput`/`ValidateReservationContext` types) along
 * with its index.ts re-exports. See docs/reservation-validation.md §13.
 */
export const validateReservationMachine = setup({
  types: {
    context: {} as ValidateReservationContext,
    input: {} as ValidateReservationInput,
    output: {} as ReservationValidations | null,
  },
  actors: {
    validateRequest: fromPromise<
      ReservationValidations,
      { reservation: unknown }
    >(async ({ input, signal }) => {
      if (input.reservation == null) {
        throw new Error('validateReservation: missing reservation payload');
      }
      return firstValueFrom(
        reservationService.validateReservation(input.reservation, signal),
      );
    }),
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QDcCGAbAlhVAXMASnGAE5q6YD2AdgHRpY4XVQDEENYtm1ylA1lwbY8hMAEcArnFwBtAAwBdRKAAOlWJgo0VIAB6IAzAA5DtAOwAWS+dPnD8w-asAaEAE9ExgIy15lgFZvQ0sATnl5UICHSwBfWLdhJjFYUnIqOiS8HjZSEkoSWlV0PAAzAoBbegwRfCIpGQVlJBB1TW1qXQMEEzMrGzsHJydLN08Eb3taQ29LACY5wwA2c29Ques5+ISQakoIOF0suuIybJ0Wtq0MrsQAWiWx+6X4xJrkolSzjurGbJZdFcOrcEPMnghQmZDCFzLDzKF1rN5AFXiBjik0uc6LBJABjXFweCXDTXC6gboBUK+SbRGaGObI5bg7zyanGdbzMKmJYmFE7dGfTE-UqoTDoSQkMCAknAloUqm0GnQ4IM6KPDyISbyCz0ylBbxLSyRALGbaxIA */
  id: 'validateReservation',
  context: ({ input }) => ({
    reservation: input.reservation,
    result: null,
  }),
  initial: 'validating',
  states: {
    validating: {
      invoke: {
        id: 'validateRequest',
        src: 'validateRequest',
        input: ({ context }) => ({ reservation: context.reservation }),
        onDone: {
          target: 'success',
          actions: assign({
            result: ({ event }) => event.output,
          }),
        },
        onError: { target: 'failure' },
      },
    },
    success: {
      type: 'final',
      output: ({ context }) => context.result,
    },
    failure: { type: 'final' },
  },
});
